import { Link } from "react-router-dom";
import { useStudents } from "@/hooks/useStudents";
import { useDongClaims } from "@/hooks/useDongClaims";
import { fmtYearMonth } from "@/lib/format";
import { unclaimedStudents } from "@/lib/claims";
import NotifyFailures from "@/components/NotifyFailures";
import DeliveryIssues from "@/components/DeliveryIssues";
import BillingDue from "@/components/BillingDue";
import NotifyRate from "@/components/NotifyRate";
import OrgContactMissing from "@/components/OrgContactMissing";

export default function AdminHome() {
  const { students } = useStudents();
  const ym = fmtYearMonth();
  const { claims } = useDongClaims(ym);

  const total = students.length;
  const courier = students.filter((s) => s.courierFlag).length;
  const byGu = students.reduce<Record<string, number>>((acc, s) => {
    acc[s.gu || "미상"] = (acc[s.gu || "미상"] || 0) + 1;
    return acc;
  }, {});
  const geocoded = students.filter((s) => s.lat && s.lng).length;
  // 배송 경로에서 누락되는 학생: 직배송인데 지도 위치 또는 동 이름 미상
  const atRisk = students.filter((s) => !s.courierFlag && (!s.lat || !s.lng || !s.dong));
  const unclaimed = unclaimedStudents(students, claims).length;

  return (
    <div className="space-y-6">
      <DeliveryIssues />
      <NotifyFailures />
      <OrgContactMissing />
      <BillingDue />
      <NotifyRate />
      {atRisk.length > 0 && (
        <Link
          to="/admin/address-confirm"
          className="block card p-4 border border-rose-200 bg-rose-50 hover:bg-rose-100 transition"
        >
          <div className="font-bold text-rose-700">⚠️ 배송 누락 위험 {atRisk.length}명 — 주소 확인 필요</div>
          <div className="text-xs text-rose-600 mt-1">
            지도 위치 또는 동 이름이 없어 배송 경로에서 빠집니다. 눌러서 주소 확인 대기로 이동 →
          </div>
        </Link>
      )}

      {total === 0 && (
        <Link to="/admin/import" className="block card p-5 border-2 border-milk-400 bg-milk-50 hover:bg-milk-100 transition">
          <div className="font-bold text-milk-800 text-base">먼저 학생 명단을 올려 주세요 →</div>
          <div className="text-sm text-milk-700 mt-1">학교에서 받은 엑셀 파일을 「데이터 가져오기」에 올리면 주소를 정리하고 배송 경로를 짤 수 있습니다. 1분이면 됩니다.</div>
        </Link>
      )}
      <section className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card title="전체 학생" value={total} />
        {/* 맡은 동네 옆에 아무도 안 맡은 학생 수(대표 결정 Q23) — 눌러서 담당 정하기로 */}
        <Link to="/admin/assign" className="card p-4 hover:bg-milk-50 transition">
          <div className="label">이번 달 맡은 동네</div>
          <div className="text-2xl font-extrabold mt-1 text-milk-700">
            {claims.length} <span className="text-sm font-medium text-slate-400">곳</span>
          </div>
          <div className={`text-xs mt-1 font-semibold ${unclaimed > 0 ? "text-rose-600" : "text-emerald-600"}`}>
            {unclaimed > 0 ? `아무도 안 맡은 학생 ${unclaimed}명 — 담당 정하기 →` : "모든 학생에 담당 기사가 있습니다"}
          </div>
        </Link>
        <Card title="월말 택배 예정" value={courier} tone="warning" />
        <Card title="지도 위치 찾은 학생" value={`${geocoded}/${total}`} tone={geocoded < total ? "warning" : undefined} />
      </section>

      <section>
        <h2 className="font-bold mb-2">구별 현황</h2>
        <div className="card p-4 grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm">
          {Object.entries(byGu)
            .sort((a, b) => b[1] - a[1])
            .map(([gu, n]) => (
              <div key={gu} className="flex justify-between bg-slate-50 rounded-lg px-3 py-2">
                <span>{gu}</span>
                <strong>{n}명</strong>
              </div>
            ))}
        </div>
      </section>

      <section className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <QuickLink to="/admin/import" title="데이터 가져오기" desc="엑셀 올리기 → 명단 저장" />
        <QuickLink to="/admin/students" title="학생 관리" desc="주소·브랜드·상태 고치기, 지도 위치 찾기" />
        <QuickLink to="/admin/address-confirm" title="주소 확인 대기" desc="지도 위치·동 이름 미상 학생에 문자 요청 → 회신 주소 입력" />
        <QuickLink to="/admin/address-updates" title="주소 변경 처리" desc="문자·메일 붙여넣기 → 자동으로 읽기 → 적용" />
        <QuickLink to="/admin/loading-lists" title="상차 리스트 생성" desc="브랜드별/구별 박스 수 집계" />
        <QuickLink to="/admin/courier" title="월말 택배 리스트" desc="택배 대상 파일 받기" />
        <QuickLink to="/admin/inventory" title="재고 관리" desc="입고·배송·택배 차감으로 창고 보유량" />
        <QuickLink to="/admin/assign" title="담당 동네 정하기" desc="기사님별 이번 달 동네·학교 맡기기, 안 맡은 학생 찾기" />
        <QuickLink to="/admin/drivers" title="기사 통계" desc="기사별 배송량 확인" />
        <QuickLink to="/admin/users" title="사용자 권한 관리" desc="기사 초대 링크 만들기, 권한 바꾸기" />
      </section>
    </div>
  );
}

function Card({ title, value, suffix, tone }: { title: string; value: number | string; suffix?: string; tone?: "warning" }) {
  return (
    <div className="card p-4">
      <div className="label">{title}</div>
      <div className={`text-2xl font-extrabold mt-1 ${tone === "warning" ? "text-amber-600" : "text-milk-700"}`}>
        {value} <span className="text-sm font-medium text-slate-400">{suffix}</span>
      </div>
    </div>
  );
}

function QuickLink({ to, title, desc }: { to: string; title: string; desc: string }) {
  return (
    <Link to={to} className="card p-4 hover:bg-milk-50 transition">
      <div className="font-bold text-milk-700">{title}</div>
      <div className="text-xs text-slate-500 mt-1">{desc}</div>
    </Link>
  );
}
